import React, { useState, useEffect } from 'react';
import { TextField, Alert } from '@cimpress/react-components';
import EscapableModal from '../EscapableModal';
import CancelButton from '../../../generic/buttons/CancelButton';
import ConfirmButton from '../../../generic/buttons/ConfirmButton';

function initRows(metadata) {
  if (!metadata || metadata.length === 0) {
    return [];
  }
  return metadata.map(tag => ({ key: tag.key, value: tag.value || '' }));
}

function normalizeValue(value) {
  if (!value) {
    return value;
  }
  try {
    const parsed = JSON.parse(value);
    if (typeof parsed === 'object' && parsed !== null) {
      return JSON.stringify(parsed);
    }
  } catch (e) {
    // Not JSON, store as-is
  }
  return value;
}

function MetadataModal({ show, t, metadata, onConfirm, onClose }) {
  const [rows, setRows] = useState(() => initRows(metadata));

  useEffect(() => {
    if (show) {
      setRows(initRows(metadata));
    }
  }, [show, metadata]);

  const addRow = () => {
    setRows([...rows, { key: '', value: '' }]);
  };

  const removeRow = (index) => {
    const updated = [...rows];
    updated.splice(index, 1);
    setRows(updated);
  };

  const updateRow = (index, field, value) => {
    const updated = [...rows];
    updated[index] = { ...updated[index], [field]: value };
    setRows(updated);
  };

  const handleConfirm = () => {
    const result = rows
      .filter(row => row.key.trim() !== '')
      .map(row => ({
        key: row.key.trim(),
        value: normalizeValue(row.value)
      }));
    onConfirm(result);
  };

  const title = <h4>{t('toolkit.metadataModalTitle')}</h4>;

  const footer = <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
    <CancelButton onClick={onClose} />
    <ConfirmButton autoFocus onClick={handleConfirm} />
  </div>;

  const body = <div style={{ padding: '10px 15px' }}>
    <Alert status='info' message={t('toolkit.metadataInfo')} />
    {rows.length > 0 && <div style={{ display: 'flex', padding: '0 0 4px 0', gap: '8px' }}>
      <span style={{ flex: 1, fontSize: '12px', fontWeight: 600, color: '#6b7785', textTransform: 'uppercase' }}>
        {t('toolkit.metadataKey')}
      </span>
      <span style={{ flex: 2, fontSize: '12px', fontWeight: 600, color: '#6b7785', textTransform: 'uppercase' }}>
        {t('toolkit.metadataValue')}
      </span>
      <span style={{ width: '36px' }} />
    </div>}
    {rows.map((row, index) => (
      <div key={index} style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '8px' }}>
        <TextField
          style={{ flex: 1, marginBottom: 0 }}
          value={row.key}
          onChange={(e) => updateRow(index, 'key', e.target.value)}
        />
        <TextField
          style={{ flex: 2, marginBottom: 0 }}
          value={row.value}
          onChange={(e) => updateRow(index, 'value', e.target.value)}
        />
        <span
          role='button'
          tabIndex={0}
          style={{ width: '36px', minWidth: '36px', height: '36px', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
          onClick={() => removeRow(index)}
          title={t('remove')}
        >
          <i className='fa fa-trash-o text-danger' />
        </span>
      </div>
    ))}
    {rows.length === 0 && <div style={{ padding: '20px 0', textAlign: 'center', color: '#6b7785' }}>
      <i className='fa fa-tags' style={{ fontSize: '24px', marginBottom: '8px', display: 'block' }} />
      {t('toolkit.noMetadataYet')}
    </div>}
    <div style={{ marginTop: '12px' }}>
      <button
        className='btn btn-outline-primary btn-sm'
        onClick={addRow}
      >
        <i className='fa fa-plus' />&nbsp;{t('toolkit.addMetadataRow')}
      </button>
    </div>
  </div>;

  return (
    <EscapableModal
      show={show}
      style={{ width: '70%' }}
      title={title}
      footer={footer}
      closeOnOutsideClick
      closeButton={false}
      onRequestHide={onClose}
    >
      {body}
    </EscapableModal>
  );
}

export default MetadataModal;
